import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { EmptyStatePlatformHubIllustration, EmptyStateOnboarding, ActionButtonType, Button, Pagination } from "@octopusdeploy/design-system-components";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { GetProcessTemplateSummariesResponse, PlatformHubVersionControlSettingsResource, ProcessTemplateSummary, Repository, ValidateGitRefV2Response } from "@octopusdeploy/octopus-server-client";
import { IsDefaultBranch, isProtectedBranch, Permission, ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import * as React from "react";
import { useAnalyticProcessTemplateCreatedDispatch } from "~/analytics/Analytics";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { PlatformHubVcsError } from "~/areas/platformHub/PlatformHubVcsError";
import type { CreateProcessTemplateDialogProps } from "~/areas/platformHub/processTemplates/CreateProcessTemplateDialog";
import { CreateProcessTemplateDialog } from "~/areas/platformHub/processTemplates/CreateProcessTemplateDialog";
import DeleteProcessTemplate from "~/areas/platformHub/processTemplates/DeleteProcessTemplate";
import { usePlatformHubGitRef } from "~/areas/platformHub/usePlatformHubGitRef";
import { repository } from "~/clientInstance";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import Markdown from "~/components/Markdown/index";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import InternalLink from "~/components/Navigation/InternalLink/index";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { withTheme } from "~/components/Theme/index";
import WarningIcon from "~/components/WarningIcon/index";
import { getValueInStorage } from "~/hooks/localStorageHelpers";
import useLocalStorage from "~/hooks/useLocalStorage";
import { DataTable, DataTableBody, DataTableHeader, DataTableHeaderColumn, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
import DateFormatter from "~/utils/DateFormatter/index";
interface LoaderData {
    processTemplatesResponse: GetProcessTemplateSummariesResponse;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    gitRefValidationResult: ValidateGitRefV2Response | undefined;
}
interface ProcessTemplatesPageProps {
    loaderData: LoaderData;
    queryParams: ProcessTemplatesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ProcessTemplatesPageQueryParams>;
}
export interface ProcessTemplatesPageQueryParams {
    searchValue: string;
    page: number | undefined;
    pageSize: number | undefined;
    gitRef: string;
}
const defaultPageSize = 30;
const emptyProcessTemplatesResponse: GetProcessTemplateSummariesResponse = {
    ProcessTemplateSummaries: [],
    TotalResults: 0,
    ItemsPerPage: 0,
    TotalNoOfProcessTemplates: 0,
};
export async function processTemplatesPageLoader(repository: Repository, queryParams: ProcessTemplatesPageQueryParams): Promise<LoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    if (!persistenceSettings.Url) {
        return {
            processTemplatesResponse: emptyProcessTemplatesResponse,
            persistenceSettings,
            gitRefValidationResult: undefined,
        };
    }
    let gitRef = queryParams.gitRef;
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    }
    const gitRefValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    if (gitRefValidationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return {
            processTemplatesResponse: emptyProcessTemplatesResponse,
            persistenceSettings,
            gitRefValidationResult,
        };
    }
    const userPageSize = getValueInStorage(`Octopus.ProcessTemplatesList.PageSize`);
    const pageSize = userPageSize ? parseInt(userPageSize) : defaultPageSize;
    const processTemplatesResponse = await repository.ProcessTemplates.getSummaries({
        skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? pageSize),
        take: queryParams.pageSize ?? pageSize,
        partialName: queryParams.searchValue,
        gitRef: gitRefValidationResult.GitRef?.CanonicalName ?? gitRef,
    });
    return {
        processTemplatesResponse,
        persistenceSettings,
        gitRefValidationResult,
    };
}
export function ProcessTemplatesPage({ loaderData, queryParams, setQueryParams }: ProcessTemplatesPageProps) {
    const [gitRef, setGitRef, validationResult] = usePlatformHubGitRef(loaderData.persistenceSettings, queryParams, setQueryParams, loaderData.gitRefValidationResult);
    const [userDefaultPageSize, setUserDefaultPageSize] = useLocalStorage(`Octopus.ProcessTemplatesList.PageSize`, defaultPageSize);
    const { result: pagingData, refetch: reload } = useQuery((repository) => {
        if (!gitRef) {
            return Promise.resolve(emptyProcessTemplatesResponse);
        }
        if (validationResult && validationResult.Type !== ValidateGitRefV2ResponseType.Success) {
            return Promise.resolve(emptyProcessTemplatesResponse);
        }
        return repository.ProcessTemplates.getSummaries({
            skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? userDefaultPageSize),
            take: queryParams.pageSize ?? userDefaultPageSize,
            partialName: queryParams.searchValue,
            gitRef,
        });
    }, [gitRef, queryParams.page, queryParams.pageSize, queryParams.searchValue, userDefaultPageSize, validationResult], "Get paginated process templates", {
        initialResult: loaderData.processTemplatesResponse,
    });
    const dispatchProcessTemplateCreated = useAnalyticProcessTemplateCreatedDispatch(IsDefaultBranch(loaderData.persistenceSettings, gitRef) ?? false, isProtectedBranch(loaderData.gitRefValidationResult?.GitRef));
    const onPageChange = (page: number) => {
        setQueryParams({ ...queryParams, page });
    };
    const onPageSizeChange = (pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
        setUserDefaultPageSize(pageSize);
    };
    const onSearch = (value: string) => {
        setQueryParams({ ...queryParams, searchValue: value, page: 1 });
    };
    const { execute: deleteProcessTemplate } = useMutation({
        name: "Delete Process Template",
        action: (repository: Repository, processTemplate: ProcessTemplateSummary) => repository.ProcessTemplates.del({ ProcessTemplateSlug: processTemplate.Slug, GitRef: processTemplate.GitRef, ChangeDescription: `Delete Process Template '${processTemplate.Name}'` }),
        afterComplete: async () => reload(),
    });
    if (!gitRef || !loaderData.persistenceSettings.Url) {
        return (<PageContent header={{ title: "Process Templates" }}>
                <ConfigureVersionControlOnboarding />
            </PageContent>);
    }
    const createProcessTemplateAction: PrimaryPageAction = {
        type: "custom",
        key: "Create Process Template",
        hasPermissions: isAllowed({ permission: Permission.PlatformHubEdit }),
        content: <CreateProcessTemplateButton gitRef={gitRef} dispatchProcessTemplateCreated={dispatchProcessTemplateCreated}/>,
    };
    const showOnboarding = pagingData.TotalNoOfProcessTemplates === 0;
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={loaderData.persistenceSettings}/>;
    if (validationResult !== undefined && validationResult.Type !== ValidateGitRefV2ResponseType.Success) {
        return (<PageContent header={{ title: "Process Templates", contextSelector: titleAccessory }}>
                <PlatformHubVcsError error={validationResult} gitRef={gitRef} persistenceSettings={loaderData.persistenceSettings} setGitRef={setGitRef}/>
            </PageContent>);
    }
    if (showOnboarding) {
        return (<PageContent header={{ title: "Process Templates", contextSelector: titleAccessory }}>
                <AddProcessTemplatesOnboarding primaryAction={createProcessTemplateAction}/>
            </PageContent>);
    }
    return (<PageContent header={{ title: "Process Templates", primaryAction: createProcessTemplateAction, contextSelector: titleAccessory }} filters={{
            inputs: [<FilterSearchBox value={queryParams.searchValue} onChange={onSearch} placeholder={"Filter by name..."} autoFocus={false} debounceDelay={500}/>],
        }} pagination={{
            ui: (<Pagination label="Process Templates" totalResults={pagingData.TotalResults} itemsPerPageOptions={[30, 50, 100]} selectedItemsPerPage={pagingData.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "bottom",
        }}>
            <DataTable>
                <DataTableHeader>
                    <DataTableRow>
                        <DataTableHeaderColumn>Name</DataTableHeaderColumn>
                        <DataTableHeaderColumn className={cn(styles.descriptionColumn)}>Description</DataTableHeaderColumn>
                        <DataTableHeaderColumn>Last Published Version</DataTableHeaderColumn>
                    </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                    {pagingData.ProcessTemplateSummaries.map((pt) => (<ProcessTemplateRow processTemplate={pt} deleteProcessTemplate={deleteProcessTemplate} key={pt.Id}/>))}
                </DataTableBody>
            </DataTable>
        </PageContent>);
}
const AddProcessTemplatesOnboarding = ({ primaryAction }: {
    primaryAction?: PrimaryPageAction;
}) => (<EmptyStateOnboarding title="Standardize your deployment processes" description="Process templates are resuable, standardized blocks of steps that can be used in deployment processes or runbooks with flexibility for app teams." learnMore={<ExternalLink href="process-templates">Docs: Process Templates</ExternalLink>} primaryAction={primaryAction} image={<EmptyStatePlatformHubIllustration />}/>);
const ConfigureVersionControlOnboarding = () => (<OnboardingPage title="Configure your Git connection" intro={<>
                <p>Process templates are a group of reusable steps for projects, stored in a Git repository.</p>
                <p>To add a process template, you’ll need to set up a Git connection in Platform Hub.</p>
            </>} learnMore={null} actionButtons={<Button label={"Configure Git Connection"} href={links.platformHubVersionControlSettingsPage.generateUrl()} importance="primary"/>}/>);
interface ProcessTemplateRowProps {
    processTemplate: ProcessTemplateSummary;
    deleteProcessTemplate: (processTemplate: ProcessTemplateSummary) => Promise<void>;
}
function ProcessTemplateRow({ processTemplate, deleteProcessTemplate }: ProcessTemplateRowProps) {
    return (<DataTableRow className={styles.middle}>
            <DataTableRowColumn className={styles.middle}>
                <ProcessTemplateNameCell processTemplate={processTemplate}/>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>
                <div className={styles.rowCellPadding}>
                    {processTemplate.HasError && withTheme((theme) => <WarningIcon color={theme.errorText}/>)}
                    <Markdown markup={processTemplate.Description ?? ""}/>
                </div>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>
                {processTemplate.Version && processTemplate.PublishedDate ? (<div className={styles.rowCellPadding}>
                        {processTemplate.Version} ({DateFormatter.dateToLongFormat(processTemplate.PublishedDate)})
                    </div>) : null}
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.right}>
                <div className={styles.rowCellPadding}>
                    <ProcessTemplateOverflowMenu processTemplate={processTemplate} deleteProcessTemplate={deleteProcessTemplate}/>
                </div>
            </DataTableRowColumn>
        </DataTableRow>);
}
interface ProcessTemplateNameCellProps {
    processTemplate: ProcessTemplateSummary;
}
function ProcessTemplateNameCell({ processTemplate }: ProcessTemplateNameCellProps) {
    const linkTarget = links.editProcessTemplatePage.generateUrl({ slug: processTemplate.Slug }, { gitRef: processTemplate.GitRef });
    return (<div>
            <InternalLink className={styles.nameContainer} to={linkTarget}>
                <Avatar type="template" size="medium" src={repository.Icons.getIconUrl(processTemplate.Icon)} alt="Process template icon"/>
                <div className={styles.name}>{processTemplate.Name}</div>
            </InternalLink>
        </div>);
}
interface ProcessTemplateOverflowProps {
    processTemplate: ProcessTemplateSummary;
    deleteProcessTemplate: (processTemplate: ProcessTemplateSummary) => Promise<void>;
}
function ProcessTemplateOverflowMenu({ processTemplate, deleteProcessTemplate }: ProcessTemplateOverflowProps) {
    const overFlowActions: Array<MenuItem | MenuItem[]> = [];
    overFlowActions.push(OverflowMenuItems.deleteItem("Delete", "Are you sure you want to delete this Process Template?", () => deleteProcessTemplate(processTemplate), <DeleteProcessTemplate processTemplateName={processTemplate.Name}/>));
    return <OverflowMenu menuItems={overFlowActions}/>;
}
function CreateProcessTemplateButton({ gitRef, dispatchProcessTemplateCreated }: CreateProcessTemplateDialogProps) {
    return (<OpenDialogButton disabled={false} label={"Create Process Template"} title={"Create new process template"} type={ActionButtonType.Primary}>
            <CreateProcessTemplateDialog gitRef={gitRef} dispatchProcessTemplateCreated={dispatchProcessTemplateCreated}/>
        </OpenDialogButton>);
}
const styles = {
    rowCellPadding: css({ paddingLeft: space[1] }),
    middle: css({
        verticalAlign: "middle !important",
    }),
    right: css({
        verticalAlign: "middle !important",
        float: "right",
    }),
    nameContainer: css({ display: "flex", alignItems: "center", gap: space[12] }),
    name: css({ paddingLeft: space[6] }),
    descriptionColumn: css({
        width: "40%",
    }),
};
